import React, { useEffect, useContext, useState } from 'react';
import { string, arrayOf } from 'prop-types';

import { useConfigService } from '@thd-nucleus/experience-context';
import { extend, QueryProvider, QueryContext, useDataModel } from '@thd-nucleus/data-sources';
import { ImpressionProvider } from '@thd-olt-component-react/impression';

import { BundlesDataModel } from '../models/dataModel';
import {
  filterHiddenDiscontinuedProducts,
  getBundleFeatures,
  getBundleType,
  getComponentImpressionData,
  getExperienceType,
  getUpdatedBundle
} from '../utils/product-utils';
import { getTemplate, getNormalizedProps } from '../subcomponents/templates/index';

const BUNDLE_PAGE_SIZE = 16;

const BundleMini = ({ bundleIds, name }) => {
  const { defaultVariables } = useContext(QueryContext);
  const configExperienceType = useConfigService('bundleExperience') || null;
  const configBundleType = useConfigService('bundleType') || null;
  const configMultiParentEnabled = useConfigService('multiParentEnabled') || null;
  const [offsetBundleIds, setOffsetBundleIds] = useState(0);
  const limitedBundleIds = bundleIds?.slice(offsetBundleIds, offsetBundleIds + BUNDLE_PAGE_SIZE) || [];

  const { data, loading } = useDataModel('products', {
    variables: {
      itemIds: limitedBundleIds
    },
    skip: limitedBundleIds.length === 0
  });

  const bundles = filterHiddenDiscontinuedProducts(data?.products) || [];

  const triggerErrorEvent = () => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('bundle-mini.error');
  };

  const noBundles = !loading && bundles.length === 0;
  const hasMoreBundles = offsetBundleIds + BUNDLE_PAGE_SIZE < bundleIds.length;
  const canPaginate = hasMoreBundles && offsetBundleIds === 0;

  useEffect(() => {
    if (noBundles && canPaginate) {
      setOffsetBundleIds((prev) => prev + BUNDLE_PAGE_SIZE);
    } else if (noBundles) {
      triggerErrorEvent();
    }
  }, [noBundles, canPaginate]);

  // Don't render if no bundles and no more to try
  if (noBundles && !canPaginate) {
    return null;
  }

  if (loading) {
    return null;
  }

  const experienceType = configExperienceType || getExperienceType(bundles?.[0]);
  const bundleType = configBundleType || getBundleType(bundles?.[0]);

  const bundleFeatures = getBundleFeatures(bundleType, experienceType);
  const updatedBundles = bundles?.map((bundle) => getUpdatedBundle(bundle, bundleFeatures));

  const { singleParent, multiParent } = bundleFeatures?.templates || {};
  const showMultiParent = configMultiParentEnabled && multiParent && updatedBundles?.length > 1;

  const templateName = showMultiParent ? multiParent : singleParent;
  const ItemTemplate = getTemplate(templateName);

  if (!ItemTemplate) {
    return null;
  }

  const normalizedProps = getNormalizedProps({
    bundles: updatedBundles,
    isMultiParent: showMultiParent,
    bundleLoading: loading,
    onError: triggerErrorEvent
  });

  return (
    <ImpressionProvider
      data={getComponentImpressionData({
        bundles: updatedBundles,
        slotId: name,
        isMultiParentEnabled: showMultiParent
      })}
    >
      <QueryProvider
        cacheKey={`${showMultiParent ? 'bundle-mini-multi-parent' : 'bundle-mini-item'}-${templateName}`}
        defaultVariables={defaultVariables?.current}
        dataSource={showMultiParent ? 'MultiParentCarousel' : 'bundleMiniItem'}
      >
        <ItemTemplate
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...normalizedProps}
        />
      </QueryProvider>
    </ImpressionProvider>
  );
};

BundleMini.propTypes = {
  bundleIds: arrayOf(string.isRequired),
  name: string
};

BundleMini.defaultProps = {
  bundleIds: [],
  name: ''
};

BundleMini.displayName = 'BundleMini';

BundleMini.dataModel = extend(
  BundlesDataModel
);

export { BundleMini };